import {
  Root,
  Portal,
  PopoverContent,
  PopoverContentProps,
  PopoverProps as RadixPopoverProps,
  PopoverClose,
  Anchor,
  PopoverAnchorProps
} from '@radix-ui/react-popover';
import { Slot } from '@radix-ui/react-slot';
import { ReactNode, useRef, useState } from 'react';
import { CSSObject } from '@emotion/react';
import { cn } from '@anytag/shared-utils';

export interface PopoverInnerProps {
  isOpen?: boolean;
  PopoverClose: typeof PopoverClose;
  closePopover: () => void;
  togglePopover: () => void;
  setOpen: () => void;
}
export interface PopoverProps
  extends Omit<RadixPopoverProps, 'opened' | 'children'>,
    Omit<PopoverContentProps, 'children'> {
  defaultOpen?: boolean;
  withBorder?: boolean;
  targetProps?: PopoverAnchorProps & { css?: CSSObject };
  contentProps?: PopoverContentProps & { css?: CSSObject };
  target: ((props: PopoverInnerProps) => ReactNode) | ReactNode;
  children: ((props: PopoverInnerProps) => ReactNode) | ReactNode;
  onClose?: () => void;
  withinPortal?: boolean;
}

export const Popover = ({
  defaultOpen = false,
  target,
  children,
  sideOffset = 4,
  contentProps,
  withBorder,
  targetProps,
  onClose,
  withinPortal = true,
  align = 'start',
  side = 'bottom',
  ...baseProps
}: PopoverProps) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  const anchorRef = useRef<HTMLDivElement | null>(null);

  const innerProps: PopoverInnerProps = {
    isOpen,
    PopoverClose,
    closePopover: () => setIsOpen(false),
    togglePopover: () => setIsOpen(!isOpen),
    setOpen: () => setIsOpen(true)
  };

  const ContentWrapper = withinPortal ? Portal : Slot;

  return (
    <Root modal={false} open={isOpen} onOpenChange={setIsOpen} {...baseProps}>
      <Anchor
        ref={anchorRef}
        {...targetProps}
        onClick={(event) => {
          targetProps?.onClick?.(event);

          // If the popover is already open, prevent it from closing
          if (isOpen) {
            event.preventDefault();
          }
        }}
      >
        {typeof target === 'function' ? target(innerProps) : target}
      </Anchor>

      <ContentWrapper {...(withinPortal ? { container: document.body } : {})}>
        <PopoverContent
          side={side}
          align={align}
          sideOffset={sideOffset}
          onInteractOutside={(e) => {
            // Prevent closing if clicking the anchor
            if (anchorRef.current && e.target instanceof Node && anchorRef.current.contains(e.target)) {
              e.preventDefault();
              return;
            }
            setIsOpen(false);
            onClose?.();
          }}
          onWheel={(e) => {
            e.stopPropagation(); // Prevent modal from intercepting wheel events
          }}
          {...contentProps}
          className={cn(
            'z-[60] rounded-sm bg-white',
            withBorder ? 'border-base-dark-gray' : 'menu-filter-shadow',
            contentProps?.className
          )}
        >
          {typeof children === 'function' ? children(innerProps) : children}
        </PopoverContent>
      </ContentWrapper>
    </Root>
  );
};
